
import moment from 'moment';
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  Connection, ProgressDetails, ProgressType,
} from '@/types';
import EditMUP1Dialog from '@/components/EditMUP1Dialog.vue';
import EditMUP2Dialog from '@/components/EditMUP2Dialog.vue';
import EditENEADialog from '@/components/connections/EditENEADialog.vue';
import AttachmentStatusLabel from '@/components/AttachmentStatusLabel.vue';
import TooltipStatusLabel from '@/components/TooltipStatusLabel.vue';

moment.locale('it');

@Component({
  components: {
    EditMUP1Dialog,
    EditMUP2Dialog,
    AttachmentStatusLabel,
    TooltipStatusLabel,
    EditENEADialog,
  },
})
export default class ConnectionProgress extends Vue {
  @Prop(Object) readonly connection!: Connection;
  @Prop(String) readonly type!: ProgressType;

  loading = false
  editDialog = false

  details: ProgressDetails | null = null;

  getDetailsTimeout: number | undefined

  @Watch('editDialog')
  onEditDialogChange(val: boolean) {
    if (val === false) {
      this.$nextTick(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.$refs.tippyRef as any).tippy().hide();

        // Deseleziona la Connection selezionata in precedenza
        this.$store.dispatch('unselectConnection');
      });
    }
  }

  get poolUpdating(): { [key: number]: boolean } {
    return this.$store.getters.currentConnectionssPoolUpdating;
  }

  get amIUpdating(): boolean {
    return this.connection
      && this.connection.id in this.poolUpdating
      && this.poolUpdating[this.connection.id] === true;
  }

  @Watch('amIUpdating')
  onAmIUpdatingChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onAmIUpdatingChange() val = ${val} oldVal ${oldVal}`); // eslint-disable-line no-console

    if (oldVal === true && val === false) {
      // La commessa è stata aggiornata
      // Se sono stati scaricati in precedenza dei dettagli...
      if (this.details) {
        // ... recupero la nuova versione dei contatti
        this.getDetails();
      }
    }
  }

  get progress(): number {
    switch (this.type) {
      case 'mup1':
        return this.connection.mup1 * 100;

      case 'mup2':
        return this.connection.mup2 * 100;

      case 'enea':
        return this.connection.enea * 100;

      default:
        break;
    }

    return 0;
  }

  // this.type === 'enea' && this.praticaEnea === 'na'
  get isEnaPracticeNa(): boolean {
    return this.type === 'enea' && this.praticaEnea === 'na';
  }

  get progressColor(): string {
    // Durante il caricamento, mostra in grigio il progresso
    if (this.loading || this.isEnaPracticeNa) return 'grey';

    // Se il progresso è completo, lo mostra verde
    return this.progress === 100 ? 'success' : 'orange';
  }

  get completedDate(): string | null {
    let completedDateValue: Date | null = null;
    if (this.type === 'mup1') completedDateValue = this.connection.mup1CompletedDate;
    else if (this.type === 'mup2') completedDateValue = this.connection.mup2CompletedDate;
    else if (this.type === 'enea') completedDateValue = this.connection.eneaCompletedDate;

    return completedDateValue && moment(completedDateValue).isValid()
      ? moment(completedDateValue).format('L')
      : null;
  }

  get mup1(): string | null {
    return this.connection.mup1CompletedDate
      && moment(this.connection.mup1CompletedDate).isValid()
      ? moment(this.connection.mup1CompletedDate).format('L')
      : null;
  }

  get mup2(): string | null {
    return this.connection.mup2CompletedDate
      && moment(this.connection.mup2CompletedDate).isValid()
      ? moment(this.connection.mup2CompletedDate).format('L')
      : null;
  }

  get enea(): string | null {
    return this.connection.eneaCompletedDate
      && moment(this.connection.eneaCompletedDate).isValid()
      ? moment(this.connection.eneaCompletedDate).format('L')
      : null;
  }

  get praticaEnea(): string {
    return this.connection?.praticaEnea ?? '';
  }

  get columnClass(): string {
    // Se il tipo di Progress è enea e la pratica è Non applicabile colora la colonna di grigio
    if (this.isEnaPracticeNa) return 'grey lighten-3';
    // Se il progress è 100 colora la colonna di verde
    return this.progress === 100 ? 'green' : '';
  }

  async getDetails() {
    try {
      this.details = await this.$store.dispatch('getConnectionProgressDetails', {
        id: this.connection?.id,
        type: this.type,
      });
    } catch (error) {
      console.log(`${this.constructor.name}: getDetails() error =`, error); // eslint-disable-line no-console
    }

    return Promise.resolve();
  }

  isAnObject(value: unknown): boolean {
    return typeof value === 'object';
  }

  onTooltipShow() {
    console.log(`${this.constructor.name}: onTooltipShow()`); // eslint-disable-line no-console

    // I dettagli sono stati recuperati precedentemente, mi fermo.
    if (this.details) return;

    this.getDetailsTimeout = setTimeout(() => {
      console.log(`${this.constructor.name}: onTooltipShow() timeout!`); // eslint-disable-line no-console

      // Recupera i dettagli
      this.getDetails();
    }, 1000);
  }

  onTooltipHide() {
    console.log(`${this.constructor.name}: onTooltipHide()`); // eslint-disable-line no-console

    clearTimeout(this.getDetailsTimeout);
  }

  async onEdit() {
    // Non permette l'apertura del dialog durante il caricamento
    // oppure  il tipo di Progress è 'enea' e la pratica è Non applicabile
    if (this.loading || this.isEnaPracticeNa) return;

    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    // Chiude eventuali tooltip "tippy" aperti
    this.$nextTick(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.$refs.tippyRef as any).tippy().hide();
    });

    // La connessione deve esistere per procedere
    if (this.connection) {
      // Seleziona la Connection prima di aprire il dialog
      this.loading = true;
      await this.$store.dispatch('selectConnection', { id: this.connection.id });
      this.editDialog = true;
      this.loading = false;
    }
  }
}

